import { useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BriefcaseBusiness, RefreshCw } from 'lucide-react';
import Navigation from '../feature/Navigation';
import Footer from '../feature/Footer';
import LockedCard from '../gating/LockedCard';
import { GateLoading } from '../gating/FeatureGate';
import { useAuth } from '../../hooks/useAuth';
import { useEntitlements } from '../../hooks/useEntitlements';
import { isProfessional } from '../../utils/isProfessional';

interface ProfessionalRouteProps {
  children: ReactNode;
  /** Already-translated overrides for the locked card. Default: the generic professional copy. */
  lockedTitle?: string;
  lockedBody?: string;
}

/**
 * Route guard for professional-only pages (the Requests board).
 *
 * - Guest: sent to /signin, returning here afterwards.
 * - Professional by session (role, accountType, declared roles, team
 *   membership): the page renders at once, with no request.
 * - Anyone else: GET /api/entitlements decides, because live professional
 *   activities are known only to the server. Not professional → a locked card
 *   inside the normal page chrome, pointing to the professional upgrade.
 *
 * The backend enforces the same rule (requireProfessional, 403
 * PROFESSIONAL_ACCOUNT_REQUIRED); this guard only spares the round trip.
 */
export default function ProfessionalRoute({ children, lockedTitle, lockedBody }: ProfessionalRouteProps) {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const { t, i18n } = useTranslation('gating');
  const sessionPro = isProfessional(user);
  const entitlements = useEntitlements({ enabled: Boolean(user) && !sessionPro });
  const [retrying, setRetrying] = useState(false);

  if (authLoading) return <GateLoading fullScreen />;

  if (!user) return <Navigate to="/signin" state={{ from: location }} replace />;

  if (sessionPro || entitlements.isProfessional) return <>{children}</>;

  if (entitlements.loading) return <GateLoading fullScreen />;

  const retry = () => {
    setRetrying(true);
    entitlements.refresh().finally(() => setRetrying(false));
  };

  // With professional roles on, an existing account adds a role at
  // /account/professional; without them, the only route in is a broker signup.
  const proRolesEnabled = import.meta.env.VITE_PRO_ROLES_ENABLED === 'true';

  return (
    <div dir={i18n.dir()} className="flex min-h-screen flex-col bg-[var(--pv-color-surface)]">
      <Navigation />
      <main id="main-content" className="flex-1 px-4 py-12 sm:px-6 sm:py-16">
        {entitlements.error ? (
          <LockedCard
            icon={RefreshCw}
            title={t('pro.checkFailed')}
            body={t('pro.checkFailedBody')}
            primary={{ label: t('pro.retry'), onClick: retry, busy: retrying }}
            secondary={{ label: t('pro.backHome'), to: '/' }}
          />
        ) : (
          <LockedCard
            icon={BriefcaseBusiness}
            title={lockedTitle ?? t('pro.title')}
            body={lockedBody ?? t('pro.body')}
            primary={
              proRolesEnabled
                ? { label: t('pro.upgrade'), to: '/account/professional' }
                : { label: t('pro.register'), to: '/register?role=BROKER' }
            }
            secondary={{ label: t('pro.backHome'), to: '/' }}
          />
        )}
      </main>
      <Footer />
    </div>
  );
}
